import { affineCorners, describeAccuracy, fitAffine } from "@festipal/shared";
import {
  LngLatBounds,
  Map as MapLibreMap,
  Marker,
  NavigationControl,
} from "maplibre-gl";
import { useCallback, useEffect, useRef, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";

import { ApiError, festivals } from "./api";
import { loadBasemap } from "./basemap";

/**
 * Calage du plan fourni par l'organisateur.
 *
 * Le geste est volontairement simple : cliquer un repère sur le plan, puis le
 * même endroit sur la carte. Trois paires suffisent — deux laisseraient la
 * rotation indéterminée.
 *
 * L'écart de chaque repère est affiché en mètres : c'est ce qui permet de repérer
 * une erreur de pointage, qu'un plan de travers ne révèle pas à l'œil.
 */

type Point = { x: number; y: number; latitude: number; longitude: number };

const ACCURACY_LABELS: Record<string, string> = {
  excellent: "excellent",
  bon: "bon",
  approximatif: "approximatif",
  mauvais: "mauvais — vérifiez vos repères",
};

export function PlanEditor({ slug }: { slug: string }) {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapLibreMap | null>(null);
  const markers = useRef<Marker[]>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [residuals, setResiduals] = useState<number[]>([]);
  const [rmse, setRmse] = useState<number | null>(null);
  const [pendingImagePoint, setPendingImagePoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [imageVersion, setImageVersion] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [overlay, setOverlay] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const plan = await festivals.plan(slug);
      setSize({ width: plan.width, height: plan.height });
      setPoints(plan.controlPoints);
      setResiduals(plan.residuals);
      setRmse(plan.rmseM);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setSize(null);
        setPoints([]);
        setResiduals([]);
        setRmse(null);
      }
    }
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let instance: MapLibreMap | null = null;
    let abandoned = false;

    void loadBasemap().then((basemap) => {
      // Le composant peut avoir été démonté pendant la lecture de l'archive.
      if (abandoned || !mapContainer.current) return;

      instance = new MapLibreMap({
        container: mapContainer.current,
        style: basemap.style,
        center: basemap.center,
        zoom: basemap.zoom,
      });
      instance.addControl(new NavigationControl(), "top-right");
      map.current = instance;
      // Les repères déjà posés attendent que la carte existe pour être reportés.
      setMapReady(true);
    });

    return () => {
      abandoned = true;
      instance?.remove();
      map.current = null;
      setMapReady(false);
    };
  }, []);

  // Report des repères déjà posés sur la carte.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    for (const marker of markers.current) marker.remove();
    markers.current = points.map((point, index) => {
      const dot = document.createElement("div");
      dot.textContent = String(index + 1);
      dot.style.cssText = `width:24px;height:24px;border-radius:50%;background:#e8590c;
        color:#fff;display:grid;place-items:center;font:600 12px sans-serif;border:2px solid #fff`;
      return new Marker({ element: dot })
        .setLngLat([point.longitude, point.latitude])
        .addTo(instance);
    });

    if (points.length > 0) {
      const bounds = new LngLatBounds();
      for (const point of points) bounds.extend([point.longitude, point.latitude]);
      instance.fitBounds(bounds, { padding: 80, maxZoom: 17, duration: 0 });
    }
  }, [points, mapReady]);

  /**
   * Superposition du plan sur la carte.
   *
   * C'est ici que le calage se juge vraiment : un écart en mètres dit qu'il y a
   * un problème, l'image posée dit lequel. Un plan décalé de cent mètres se voit
   * d'un coup d'œil, un plan retourné aussi.
   */
  useEffect(() => {
    const instance = map.current;
    if (!instance || !size) return;

    const remove = () => {
      if (instance.getLayer("plan-cale")) instance.removeLayer("plan-cale");
      if (instance.getSource("plan-cale")) instance.removeSource("plan-cale");
    };

    // Le calage est refait ici plutôt que demandé au serveur : les repères
    // viennent d'être posés, et attendre un aller-retour ferait clignoter
    // l'image à chaque clic.
    const fitted = points.length >= 3 ? fitAffine(points) : null;
    if (!fitted || !overlay) {
      remove();
      return;
    }

    remove();
    instance.addSource("plan-cale", {
      type: "image",
      url: `${festivals.planImageUrl(slug)}?v=${imageVersion}`,
      coordinates: affineCorners(fitted.transform, size.width, size.height),
    });
    instance.addLayer(
      {
        id: "plan-cale",
        type: "raster",
        source: "plan-cale",
        paint: { "raster-opacity": 0.7 },
      },
      // Sous les repères, qui doivent rester visibles par-dessus.
      undefined,
    );

    return remove;
  }, [points, size, mapReady, overlay, slug, imageVersion]);

  const save = useCallback(
    async (next: Point[]) => {
      setPoints(next);
      try {
        const result = await festivals.saveControlPoints(slug, next);
        setResiduals(result.residuals);
        setRmse(result.rmseM);
      } catch {
        setMessage("Enregistrement impossible.");
      }
    },
    [slug],
  );

  // La carte n'accepte un clic que si un point du plan attend son correspondant.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    const onClick = (event: { lngLat: { lat: number; lng: number } }) => {
      if (!pendingImagePoint) {
        setMessage("Cliquez d'abord le repère sur le plan, puis sur la carte.");
        return;
      }
      const next = [
        ...points,
        {
          ...pendingImagePoint,
          latitude: event.lngLat.lat,
          longitude: event.lngLat.lng,
        },
      ];
      setPendingImagePoint(null);
      setMessage(null);
      void save(next);
    };

    instance.on("click", onClick);
    return () => {
      instance.off("click", onClick);
    };
  }, [pendingImagePoint, points, save, mapReady]);

  const upload = async (file: File) => {
    setMessage(null);
    try {
      const plan = await festivals.uploadPlan(slug, file);
      setSize(plan);
      setPoints([]);
      setResiduals([]);
      setRmse(null);
      setImageVersion((v) => v + 1);
      setMessage("Plan envoyé. Placez au moins trois repères.");
    } catch (cause) {
      setMessage(
        cause instanceof ApiError && cause.status === 415
          ? "Format non accepté : envoyez une image PNG, JPEG ou WebP."
          : "Envoi impossible.",
      );
    }
  };

  const accuracy = rmse === null ? null : describeAccuracy(rmse);

  return (
    <section className="card">
      <h2>Plan du site</h2>
      <p className="muted">
        Envoyez votre plan, puis désignez au moins trois repères reconnaissables : un
        clic sur le plan, puis le même endroit sur la carte. Un plan d'illustration
        n'étant pas à l'échelle, quelques mètres d'écart sont normaux.
      </p>

      <div className="row">
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
          }}
        />
        {points.length > 0 ? (
          <button
            className="danger"
            onClick={() => {
              setResiduals([]);
              void save([]);
            }}
          >
            Effacer les repères
          </button>
        ) : null}
      </div>

      {message ? <p className="muted">{message}</p> : null}

      {size ? (
        <>
          <div className="row">
            <span className="badge">
              {points.length} repère(s){points.length < 3 ? " — trois au minimum" : ""}
            </span>
            {rmse !== null && accuracy ? (
              <span
                className={accuracy === "mauvais" ? "badge pending" : "badge published"}
              >
                Écart moyen {rmse.toFixed(1)} m — {ACCURACY_LABELS[accuracy]}
              </span>
            ) : null}
            <button className="ghost" onClick={() => setOverlay((value) => !value)}>
              {overlay
                ? "Masquer le plan sur la carte"
                : "Afficher le plan sur la carte"}
            </button>
            {pendingImagePoint ? (
              <span className="badge pending">
                Repère posé sur le plan — cliquez maintenant sur la carte
              </span>
            ) : null}
          </div>

          <div className="grid-2">
            <div>
              <p className="muted">Plan</p>
              <div
                style={{
                  position: "relative",
                  border: "1px solid #dee2e6",
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <img
                  ref={imageRef}
                  src={`${festivals.planImageUrl(slug)}?v=${imageVersion}`}
                  alt="Plan du site fourni par l'organisateur"
                  style={{ width: "100%", display: "block", cursor: "crosshair" }}
                  onClick={(event) => {
                    const image = event.currentTarget;
                    const rect = image.getBoundingClientRect();
                    // L'image est affichée redimensionnée : le clic est ramené
                    // aux pixels d'origine, seuls repères que le calage connaît.
                    const scale = size.width / rect.width;
                    setPendingImagePoint({
                      x: Math.round((event.clientX - rect.left) * scale),
                      y: Math.round((event.clientY - rect.top) * scale),
                    });
                    setMessage(null);
                  }}
                />
                {points.map((point, index) => (
                  <span
                    key={`${point.x}-${point.y}-${index}`}
                    style={{
                      position: "absolute",
                      left: `${(point.x / size.width) * 100}%`,
                      top: `${(point.y / size.height) * 100}%`,
                      transform: "translate(-50%, -50%)",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "#e8590c",
                      color: "#fff",
                      border: "2px solid #fff",
                      display: "grid",
                      placeItems: "center",
                      font: "600 12px sans-serif",
                    }}
                  >
                    {index + 1}
                  </span>
                ))}
                {pendingImagePoint ? (
                  <span
                    style={{
                      position: "absolute",
                      left: `${(pendingImagePoint.x / size.width) * 100}%`,
                      top: `${(pendingImagePoint.y / size.height) * 100}%`,
                      transform: "translate(-50%, -50%)",
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "3px solid #0b7285",
                    }}
                  />
                ) : null}
              </div>
            </div>

            <div>
              <p className="muted">Carte</p>
              <div className="map" style={{ height: 360 }} ref={mapContainer} />
            </div>
          </div>

          {residuals.length > 0 ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Repère</th>
                    <th>Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {residuals.map((value, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td style={{ color: value > 40 ? "#c92a2a" : undefined }}>
                        {value.toFixed(1)} m
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : (
        <p className="muted">Aucun plan pour l'instant.</p>
      )}
    </section>
  );
}
